import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';

export interface SavedFilter {
  id: string;
  organization_id: string;
  user_id: string;
  name: string;
  description?: string;
  entity_type: 'assets' | 'audits' | 'users';
  filter_config: any;
  is_favorite: boolean;
  show_on_dashboard: boolean;
  show_in_menu: boolean;
  shared_with_org: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSavedFilterData {
  organization_id: string;
  name: string;
  description?: string;
  entity_type: 'assets' | 'audits' | 'users';
  filter_config: any;
  is_favorite?: boolean;
  show_on_dashboard?: boolean;
  show_in_menu?: boolean;
  shared_with_org?: boolean;
}

export interface UpdateSavedFilterData {
  name?: string;
  description?: string;
  filter_config?: any;
  is_favorite?: boolean;
  show_on_dashboard?: boolean;
  show_in_menu?: boolean;
  shared_with_org?: boolean;
}

/**
 * Fetch all saved filters for a user in an organization
 */
export function useSavedFilters(organizationId: string, entityType: 'assets' | 'audits' | 'users') {
  return useQuery({
    queryKey: ['saved-filters', organizationId, entityType],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('user_saved_filters')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', session.session.user.id)
        .eq('entity_type', entityType)
        .order('is_favorite', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as SavedFilter[];
    },
    enabled: !!organizationId,
  });
}

/**
 * Create a new saved filter
 */
export function useCreateSavedFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (filterData: CreateSavedFilterData) => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('user_saved_filters')
        .insert({
          ...filterData,
          user_id: session.session.user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data as SavedFilter;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['saved-filters', variables.organization_id, variables.entity_type],
      });
      notifications.show({
        title: 'Filter saved',
        message: 'Your filter has been saved successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to save filter',
        color: 'red',
      });
    },
  });
}

/**
 * Update an existing saved filter
 */
export function useUpdateSavedFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
      organizationId,
      entityType,
    }: {
      id: string;
      data: UpdateSavedFilterData;
      organizationId: string;
      entityType: 'assets' | 'audits' | 'users';
    }) => {
      const { data: result, error } = await supabase
        .from('user_saved_filters')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { result: result as SavedFilter, organizationId, entityType };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['saved-filters', data.organizationId, data.entityType],
      });
      notifications.show({
        title: 'Filter updated',
        message: 'Your filter has been updated successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update filter',
        color: 'red',
      });
    },
  });
}

/**
 * Delete a saved filter
 */
export function useDeleteSavedFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      organizationId,
      entityType,
    }: {
      id: string;
      organizationId: string;
      entityType: 'assets' | 'audits' | 'users';
    }) => {
      const { error } = await supabase.from('user_saved_filters').delete().eq('id', id);

      if (error) throw error;
      return { organizationId, entityType };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['saved-filters', data.organizationId, data.entityType],
      });
      notifications.show({
        title: 'Filter deleted',
        message: 'Your filter has been deleted successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete filter',
        color: 'red',
      });
    },
  });
}

/**
 * Toggle favorite status of a saved filter
 */
export function useToggleFavoriteSavedFilter() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      isFavorite,
      organizationId,
      entityType,
    }: {
      id: string;
      isFavorite: boolean;
      organizationId: string;
      entityType: 'assets' | 'audits' | 'users';
    }) => {
      const { data, error } = await supabase
        .from('user_saved_filters')
        .update({ is_favorite: !isFavorite })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { result: data as SavedFilter, organizationId, entityType };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['saved-filters', data.organizationId, data.entityType],
      });
      queryClient.invalidateQueries({
        queryKey: ['favorite-views', data.organizationId],
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update favorite status',
        color: 'red',
      });
    },
  });
}

/**
 * Fetch user's favorite views that should show on dashboard
 */
export function useDashboardViews(organizationId: string) {
  return useQuery({
    queryKey: ['dashboard-views', organizationId],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('user_saved_filters')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', session.session.user.id)
        .eq('show_on_dashboard', true)
        .order('name');

      if (error) throw error;
      return data as SavedFilter[];
    },
    enabled: !!organizationId,
  });
}

/**
 * Fetch user's favorite views that should show in menu
 */
export function useMenuViews(organizationId: string) {
  return useQuery({
    queryKey: ['menu-views', organizationId],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('user_saved_filters')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', session.session.user.id)
        .eq('show_in_menu', true)
        .order('name');

      if (error) throw error;
      return data as SavedFilter[];
    },
    enabled: !!organizationId,
  });
}

/**
 * Fetch views shared by others in the organization
 */
export function useSharedViews(organizationId: string) {
  return useQuery({
    queryKey: ['shared-views', organizationId],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        .from('user_saved_filters')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('shared_with_org', true)
        .neq('user_id', session.session.user.id) // Exclude own views
        .order('name');

      if (error) throw error;
      return data as SavedFilter[];
    },
    enabled: !!organizationId,
  });
}

/**
 * Fetch all favorite views (own favorites + shared views)
 */
export function useFavoriteViews(organizationId: string) {
  return useQuery({
    queryKey: ['favorite-views', organizationId],
    queryFn: async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user?.id) {
        throw new Error('User not authenticated');
      }

      // Get user's own favorite views
      const { data: ownViews, error: ownError } = await supabase
        .from('user_saved_filters')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('user_id', session.session.user.id)
        .eq('is_favorite', true)
        .order('name');

      if (ownError) throw ownError;

      return ownViews as SavedFilter[];
    },
    enabled: !!organizationId,
  });
}
